/**
 * Hybrid-AHU **Info** window (requested 2026-09-30) — the draggable "How to"
 * panel behind the rail's new Info icon (`CanvasToolbar`, the li after the
 * Delete Section trash). Copy lives in `HybridInfoContent`.
 *
 * WHY IT IS NOT A `.toolbar-item`
 * The rail's other windows are members of the single-open `openWindow` engine
 * (§2.4): one at a time, docked at `left:75px` inside `#canvas_toolbar`, closed
 * by any outside click. This one was asked for as a *window* — draggable
 * anywhere on the canvas, with its own Minimize and Close — so it is a portaled
 * `position: fixed` panel with its own state instead, and an outside click
 * leaves it alone. It opens on hover with the same 600 ms delay the docked
 * windows use, so the affordance still matches Configure Tunnel.
 *
 * GEOMETRY (the request: "same dimensions/sizing as the Configure Tunnel
 * popup", "popup width equal to the Data Bar width" — the two disagree on
 * width, so width comes from the Data Bar and everything else from Configure
 * Tunnel). Both are measured from the live DOM on first open rather than
 * hard-coded, because both are viewport-dependent:
 *   - `.toolbar-item { position:absolute; left:75px; height:100% }`
 *     (ahu/skeleton.css:592-603) ⇒ top/height = `#canvas_toolbar`'s box, left =
 *     its left + 75.
 *   - Data Bar = `#forms-main-container` (520 px at 1920×1080).
 * `DEFAULT_*` below are only the fallbacks for a DOM that has neither.
 *
 * ★ ZOOM COMPENSATION IS LOAD-BEARING, same as PsychrometricFloatingIcon.
 * The board puts CSS `zoom` on <body> (useBodyZoom) whenever the viewport is
 * under 1830×950. `left`/`top`/`width`/`height` on a fixed descendant are
 * authored in PRE-zoom space and rendered at value×zoom, while
 * getBoundingClientRect/clientX are POST-zoom. So every measurement is divided
 * by the zoom on the way in and every pointer delta on the way through, and the
 * state below is entirely in pre-zoom (layout) px.
 *
 * Minimize leaves `#hybrid-info-floating-icon` in the canvas's bottom-right
 * corner, which is the Sizer / Component Ratings / Psychrometrics floating-icon
 * behaviour: a press that never travels drags nothing and restores the window,
 * a press that does moves the icon (and, like those three, the moved position
 * is not persisted). The icon's `right` slot cascades past whichever of the
 * other three are on screen — see the `:has()` block in ahuBoardOverrides.scss.
 */

import { useCallback, useEffect, useRef, useState, type PointerEvent } from 'react';
import { createPortal } from 'react-dom';
import HybridInfoContent from './HybridInfoContent';

/** `.toolbar-item { left: 75px }` — where the docked windows start. */
const TOOLBAR_ITEM_LEFT = 75;
/** #forms-main-container at 1920×1080; only used if the panel is not mounted. */
const DEFAULT_WIDTH = 520;
const DEFAULT_TOP = 136;
const DEFAULT_LEFT = 637;
const DEFAULT_HEIGHT = 600;
/** Pointer travel before a press on the floating icon stops being a click. */
const ICON_DRAG_SLOP = 4;

export type HybridInfoState = 'closed' | 'open' | 'minimized';

interface Rect {
  left: number;
  top: number;
  width: number;
  height: number;
}

/** `document.body`'s CSS `zoom` (useBodyZoom); 1 when unset or invalid. */
const zoomFactor = (): number => {
  const z = parseFloat(
    (getComputedStyle(document.body) as CSSStyleDeclaration & { zoom?: string }).zoom ?? ''
  );
  return z && z > 0 ? z : 1;
};

/** Configure Tunnel's box, in layout px, with the Data Bar's width. */
const dockedRect = (): Rect => {
  const z = zoomFactor();
  const rail = document.getElementById('canvas_toolbar')?.getBoundingClientRect();
  const bar = document.getElementById('forms-main-container')?.getBoundingClientRect();
  return {
    left: rail ? rail.left / z + TOOLBAR_ITEM_LEFT : DEFAULT_LEFT,
    top: rail ? rail.top / z : DEFAULT_TOP,
    width: bar ? bar.width / z : DEFAULT_WIDTH,
    height: rail ? rail.height / z : DEFAULT_HEIGHT,
  };
};

/** Keep the whole window inside the viewport, in layout px. */
const clampRect = (rect: Rect): Rect => {
  const z = zoomFactor();
  const maxLeft = Math.max(0, window.innerWidth / z - rect.width);
  const maxTop = Math.max(0, window.innerHeight / z - rect.height);
  return {
    ...rect,
    left: Math.min(Math.max(0, rect.left), maxLeft),
    top: Math.min(Math.max(0, rect.top), maxTop),
  };
};

interface HybridInfoPopupProps {
  state: HybridInfoState;
  onMinimize: () => void;
  onRestore: () => void;
  onClose: () => void;
}

const HybridInfoPopup = ({ state, onMinimize, onRestore, onClose }: HybridInfoPopupProps) => {
  // null until the first open — the rail/Data Bar have to be laid out before
  // they can be measured, and the window keeps wherever the user dragged it
  // across a minimize/restore (and a close/re-open) for the rest of the session.
  const [rect, setRect] = useState<Rect | null>(null);
  const rectRef = useRef<Rect | null>(null);
  rectRef.current = rect;
  const drag = useRef<{ pointerId: number; startX: number; startY: number; base: Rect } | null>(
    null
  );

  useEffect(() => {
    if (state !== 'open' || rectRef.current) return;
    setRect(clampRect(dockedRect()));
  }, [state]);

  // A resize can strand the window off-screen (and changes the zoom that its
  // coordinates are expressed in); re-clamp rather than re-dock, so a dragged
  // position survives.
  useEffect(() => {
    const onResize = () => setRect((r) => (r ? clampRect(r) : r));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const onHeaderPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    const base = rectRef.current;
    // Buttons live in the header; a press on one must not start a drag.
    if (e.button !== 0 || !base || (e.target as HTMLElement).closest('button')) return;
    drag.current = { pointerId: e.pointerId, startX: e.clientX, startY: e.clientY, base };
    e.currentTarget.setPointerCapture(e.pointerId);
    e.preventDefault();
  };

  const onHeaderPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    if (!d || e.pointerId !== d.pointerId) return;
    const z = zoomFactor();
    setRect(
      clampRect({
        ...d.base,
        left: d.base.left + (e.clientX - d.startX) / z,
        top: d.base.top + (e.clientY - d.startY) / z,
      })
    );
  };

  const onHeaderPointerUp = (e: PointerEvent<HTMLDivElement>) => {
    if (drag.current?.pointerId !== e.pointerId) return;
    drag.current = null;
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
  };

  if (state === 'closed') return null;

  // Legacy target of every other floating popup on this board
  // (ComboLouverHowTo and the two selector how-tos): `.rotate-card` carries the
  // board's card-flip `transform`, which would otherwise become the containing
  // block for `position: fixed` and put this ~490px off and clipped.
  const host = document.getElementById('ksdm-content') ?? document.body;

  return createPortal(
    <>
      {state === 'open' && rect && (
        <div
          id="hybrid-info-popup"
          className="hybrid-info-popup"
          style={{ left: rect.left, top: rect.top, width: rect.width, height: rect.height }}
        >
          {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
          <div
            className="hybrid-info-header"
            onPointerDown={onHeaderPointerDown}
            onPointerMove={onHeaderPointerMove}
            onPointerUp={onHeaderPointerUp}
            onPointerCancel={onHeaderPointerUp}
          >
            {/* Requested 2026-09-30: no "Info" title — the header names the
                document instead, on one line, so it stays visible while the body
                scrolls. */}
            <span className="hybrid-info-title">How to - Hybrid Air Handling Unit</span>
            <button
              type="button"
              className="hybrid-info-minimize-btn"
              title="Minimize"
              onClick={onMinimize}
            >
              &minus;
            </button>
            {/* Requested 2026-09-30: the Psychrometrics popup's pair exactly —
                so Close is its word-button (`.psy-close-btn`, uppercased in
                CSS), not the &times; glyph this used to carry. */}
            <button
              type="button"
              className="hybrid-info-close-btn"
              title="Close"
              onClick={onClose}
            >
              Close
            </button>
          </div>
          <div className="hybrid-info-body">
            <HybridInfoContent />
          </div>
        </div>
      )}
      <HybridInfoFloatingIcon active={state === 'minimized'} onRestore={onRestore} />
    </>,
    host
  );
};

/**
 * `#hybrid-info-floating-icon` — the minimized state, bottom-right of the
 * canvas. Same contract as `#psy-floating-icon` (PsychrometricFloatingIcon):
 * `.active` shows it, a press under the slop threshold IS the click that
 * restores, and all drag math is done in visual space and divided by the body
 * zoom once, on the way into CSS.
 */
const HybridInfoFloatingIcon = ({
  active,
  onRestore,
}: {
  active: boolean;
  onRestore: () => void;
}) => {
  const elRef = useRef<HTMLDivElement | null>(null);
  const drag = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    visualLeft: number;
    visualTop: number;
    moved: boolean;
  } | null>(null);

  const onPointerDown = useCallback((e: PointerEvent<HTMLDivElement>) => {
    const el = elRef.current;
    if (!el || e.button !== 0) return;
    const r = el.getBoundingClientRect();
    drag.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      visualLeft: r.left,
      visualTop: r.top,
      moved: false,
    };
    el.setPointerCapture(e.pointerId);
    e.preventDefault();
  }, []);

  const onPointerMove = useCallback((e: PointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    const el = elRef.current;
    if (!d || !el || e.pointerId !== d.pointerId) return;
    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;
    if (Math.abs(dx) > ICON_DRAG_SLOP || Math.abs(dy) > ICON_DRAG_SLOP) d.moved = true;
    if (!d.moved) return;
    const r = el.getBoundingClientRect();
    const z = zoomFactor();
    const left = Math.min(Math.max(0, d.visualLeft + dx), window.innerWidth - r.width);
    const top = Math.min(Math.max(0, d.visualTop + dy), window.innerHeight - r.height);
    el.style.left = `${left / z}px`;
    el.style.top = `${top / z}px`;
    el.style.right = 'auto';
    el.style.bottom = 'auto';
  }, []);

  const onPointerUp = useCallback(
    (e: PointerEvent<HTMLDivElement>) => {
      const d = drag.current;
      const el = elRef.current;
      drag.current = null;
      if (!d || !el) return;
      if (el.hasPointerCapture(e.pointerId)) el.releasePointerCapture(e.pointerId);
      if (!d.moved) onRestore();
    },
    [onRestore]
  );

  return (
    <div
      ref={elRef}
      id="hybrid-info-floating-icon"
      className={`hybrid-info-floating-icon${active ? ' active' : ''}`}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
    >
      <i className="icon-info" />
      {/* Requested 2026-09-30 — same wording as the rail button's tooltip
          (CanvasToolbar), so both affordances name the same window. */}
      <span className="icon-title">How to Hybrid</span>
    </div>
  );
};

export default HybridInfoPopup;
